import type { GenerationListItem } from '@stratumn/studio-shared';
import {
  GenerationListItemSchema,
  GenerationMetadataSchema
} from '@stratumn/studio-shared';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { generationKeys } from '@/lib/query-keys';
import type { GenerationFilters } from '@/pages/Settings/generation/filters/types';
import { HTTPWrapper } from '@/utils/http';

const ITEMS_PER_PAGE = 7;
const POLL_INTERVAL = 5000;

const http = new HTTPWrapper('process');

const GenerationListSchema = z.array(GenerationListItemSchema);

const buildFilterParams = (
  params: URLSearchParams,
  filters?: GenerationFilters
): void => {
  if (!filters) {
    return;
  }
  if (filters.workspace_id.values.length > 0) {
    for (const id of filters.workspace_id.values) {
      params.append('workspace_id', id);
    }
    params.set('workspace_id_op', filters.workspace_id.operator);
  }
  if (filters.version_group.values.length > 0) {
    for (const group of filters.version_group.values) {
      params.append('version_group', group);
    }
    params.set('version_group_op', filters.version_group.operator);
  }
  if (filters.created_by.values.length > 0) {
    for (const id of filters.created_by.values) {
      params.append('created_by', id);
    }
    params.set('created_by_op', filters.created_by.operator);
  }
};

const fetchGenerations = async (
  processId: string,
  page: number,
  take: number,
  filters?: GenerationFilters
): Promise<GenerationListItem[]> => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('take', String(take));
  buildFilterParams(params, filters);

  const response = await http.get<unknown>(
    `/${processId}/generations?${params.toString()}`
  );

  // Validate the API response so backend drift surfaces as a typed error
  // rather than as `undefined` silently flowing into the UI.
  return GenerationListSchema.parse(response ?? []);
};

const fetchGenerationsCount = async (
  processId: string,
  filters?: GenerationFilters
): Promise<number> => {
  const params = new URLSearchParams();
  buildFilterParams(params, filters);

  const queryString = params.toString();
  const url = `/${processId}/generations/count${queryString ? `?${queryString}` : ''}`;

  const response = await http.get<unknown>(url);
  if (response == null) {
    return 0;
  }
  const parsed = GenerationMetadataSchema.parse(response);
  return parsed.numberOfGenerations;
};

type UseGenerationsParams = {
  processId: string;
  page?: number;
  take?: number;
  filters?: GenerationFilters;
  /** Disable the queries entirely (e.g. while processId is unknown). */
  enabled?: boolean;
  /**
   * When true (default), also issues a `/generations/count` request so the
   * caller can paginate. Consumers that don't paginate (e.g. "show last N")
   * should pass `false` to skip the extra round-trip.
   */
  withCount?: boolean;
};

type UseGenerationsResult = {
  generations: GenerationListItem[];
  count: number;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  numberOfPages: number;
  dataUpdatedAt: number;
  refetch: () => void;
};

export const useGenerations = ({
  processId,
  page = 1,
  take = ITEMS_PER_PAGE,
  filters,
  enabled = true,
  withCount = true
}: UseGenerationsParams): UseGenerationsResult => {
  // Avoid issuing /process//generations when the caller hasn't resolved
  // the processId yet (e.g. parent is still loading the process).
  const isEnabled = enabled && Boolean(processId);

  const listQuery = useQuery({
    queryKey: generationKeys.list(processId, { page, take, filters }),
    queryFn: () => fetchGenerations(processId, page, take, filters),
    enabled: isEnabled,
    refetchInterval: query => {
      const data = query.state.data;
      if (!data) {
        return false;
      }
      const hasInProgress = data.some(
        item => item.status === 'PENDING' || item.status === 'RUNNING'
      );
      return hasInProgress ? POLL_INTERVAL : false;
    }
  });

  const countQuery = useQuery({
    queryKey: generationKeys.count(processId, filters),
    queryFn: () => fetchGenerationsCount(processId, filters),
    enabled: isEnabled && withCount
  });

  const generations = listQuery.data ?? [];
  const count = countQuery.data ?? 0;
  const numberOfPages = Math.max(1, Math.ceil(count / take));

  return {
    generations,
    count,
    isLoading: listQuery.isLoading || (withCount && countQuery.isLoading),
    isFetching: listQuery.isFetching,
    isError: listQuery.isError || (withCount && countQuery.isError),
    error: listQuery.error ?? (withCount ? countQuery.error : null),
    numberOfPages,
    dataUpdatedAt: listQuery.dataUpdatedAt,
    refetch: () => {
      listQuery.refetch();
      if (withCount) {
        countQuery.refetch();
      }
    }
  };
};
